import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Divider,
  useTheme,
  alpha,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Switch,
  InputAdornment,
  Tooltip,
  Autocomplete,
  Radio,
  RadioGroup,
  FormHelperText,
} from '@mui/material';
import {
  Close,
  ArrowBack,
  ArrowForward,
  CheckCircle,
  Warning,
  CalendarMonth,
  Receipt,
  Info,
  LocationOn,
  Search,
  Map as MapIcon,
  SwapHoriz,
  PersonAdd,
  Block,
  Error,
  Devices,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import { Contract, Invoice, ContratoServicos, Ticket, Client, Equipment } from '../../types';
import invoiceService, { InvoiceFormData } from '../../services/invoiceService';
import { contractService } from '../../services/contractService';
import { clientService } from '../../services/clientService';
import { ContratoServicosService } from '../../services/contratoServicosService';
import { ticketService } from '../../services/ticketService';
import equipmentService from '../../services/equipmentService';
import { chamadoAssuntoService, ChamadoAssunto } from '../../services/chamadoAssuntoService';
import DiasVencimentoSelect from '../selects/DiasVencimentoSelect';
import GatewaySelect from '../selects/GatewaySelect';
import ContractServicosSelect from '../selects/ContractServicosSelect';
import EstadosBrSelect from '../selects/EstadosBrSelect';
import FuncionariosSelect from '../selects/FuncionariosSelect';
import ClientSelect from '../selects/ClientSelect';
import ClientForm from '../forms/ClientForm';
import SysDateTime from '../SysDateTime';
import { formatarCepOnInput, buscarCep, formatarValorOnInput, parsearMoeda } from '../../utils/formatToView';
import { InvoiceStatusChip } from '../../utils/faturaStatusUtils';
import { MapSelectorModal } from '../common';
import { usePermissions } from '../../contexts/AuthContext';

type AcaoWizard =
  | 'alterar_dia_vencimento'
  | 'mudanca_endereco'
  | 'alterar_titularidade'
  | 'cancelar_contrato';

// Steps dinâmicos por ação (sem a etapa de escolha, que é pré-stepper)
const STEPS_BY_ACAO: Record<AcaoWizard, string[]> = {
  cancelar_contrato: ['Pendências do contrato', 'Motivo do cancelamento'],
  alterar_dia_vencimento: [
    'Verificação',
    'Dados Atuais',
    'Fatura Proporcional',
  ],
  mudanca_endereco: [
    'Serviço',
    'Dados Novo Endereço',
    'Financeiro',
    'Chamado',
    'Finalizado',
  ],
  alterar_titularidade: [
    'Novo Titular',
    'Migração de Dados',
    'Confirmação',
  ],
};

const PRIORIDADES = [
  { value: 'baixa', label: 'Baixa' },
  { value: 'normal', label: 'Normal' },
  { value: 'alta', label: 'Alta' },
  { value: 'urgente', label: 'Urgente' },
];

const SCHEDULE_START_HOUR = 7;
const SCHEDULE_END_HOUR = 22;
const SCHEDULE_STEP_MINUTES = 30;

const toDateKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatHourLabel = (date: Date): string => {
  const hour = String(date.getHours()).padStart(2, '0');
  const minute = String(date.getMinutes()).padStart(2, '0');
  return `${hour}:${minute}`;
};

const formatPriorityLabel = (prioridade: string | undefined) => {
  const labels: Record<string, string> = {
    baixa: 'Baixa',
    normal: 'Normal',
    alta: 'Alta',
    urgente: 'Urgente',
  };
  return labels[prioridade || 'normal'] || 'Normal';
};

const getPriorityTextColor = (prioridade: string | undefined) => {
  switch (prioridade) {
    case 'baixa':
      return 'success.main';
    case 'normal':
      return 'info.main';
    case 'alta':
      return 'warning.main';
    case 'urgente':
      return 'error.main';
    default:
      return 'text.primary';
  }
};

const isFaturaPendenteCancelamento = (f: Invoice) =>
  f.status !== 'pago' && f.status !== 'cancelado';

const isChamadoPendenteWizard = (t: Ticket) =>
  t.status !== 'resolvido' && t.status !== 'fechado' && t.status !== 'cancelado';

const labelStatusChamado: Record<string, string> = {
  aberto: 'Aberto',
  em_andamento: 'Em andamento',
  resolvido: 'Resolvido',
  fechado: 'Fechado',
  cancelado: 'Cancelado',
};

/** Alinhado a `ContractEquipments` — status de provisionamento na OLT */
const getProvisionStatusColor = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'pending':
      return 'warning';
    case 'provisioned':
      return 'success';
    case 'failed':
      return 'error';
    default:
      return 'default';
  }
};

const getProvisionStatusIcon = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'pending':
      return <Warning fontSize="inherit" />;
    case 'provisioned':
      return <CheckCircle fontSize="inherit" />;
    case 'failed':
      return <Error fontSize="inherit" />;
    default:
      return <Devices fontSize="inherit" />;
  }
};

const getProvisionStatusLabel = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'pending':
      return 'Pendente';
    case 'provisioned':
      return 'Provisionado';
    case 'failed':
      return 'Falha';
    default:
      return status || 'N/A';
  }
};

interface ContractAlteracaoWizardProps {
  open: boolean;
  onClose: () => void;
  contract: Contract;
  onRefresh: () => void;
}

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('pt-BR');
};

const formatDateTime = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

const addDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

const diffDays = (start: Date, end: Date): number => {
  const msPerDay = 1000 * 60 * 60 * 24;
  return Math.round((end.getTime() - start.getTime()) / msPerDay) + 1;
};

const toDateOnly = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const buildDateWithDay = (dia: number, year: number, month: number): Date => {
  const lastDay = new Date(year, month, 0).getDate();
  const d = Math.min(dia, lastDay);
  return new Date(year, month - 1, d);
};

const ajustarFimDeSemana = (data: Date): Date => {
  const diaSemana = data.getDay(); // 0 = Domingo, 6 = Sábado
  const novaData = new Date(data);
  if (diaSemana === 0) { // Domingo -> Segunda
    novaData.setDate(novaData.getDate() + 1);
  } else if (diaSemana === 6) { // Sábado -> Segunda
    novaData.setDate(novaData.getDate() + 2);
  }
  return novaData;
};

const getProximoVencimentoTaxa = (dia: number): Date => {
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  let mes = hoje.getMonth() + 1;
  let ano = hoje.getFullYear();
  let venc = buildDateWithDay(dia, ano, mes);
  if (venc <= hoje) {
    mes++;
    if (mes > 12) {
      mes = 1;
      ano++;
    }
    venc = buildDateWithDay(dia, ano, mes);
  }
  return ajustarFimDeSemana(venc);
};

const calcProximaMensalidade = (novoDia: number): Date => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1;

  let candidate = buildDateWithDay(novoDia, currentYear, currentMonth);
  if (candidate <= today) {
    const nextMonth = currentMonth === 12 ? 1 : currentMonth + 1;
    const nextYear = currentMonth === 12 ? currentYear + 1 : currentYear;
    candidate = buildDateWithDay(novoDia, nextYear, nextMonth);
  }
  return candidate;
};

// ---------- Dados do formulário de endereço ----------
interface EnderecoFormData {
  cep: string;
  endereco: string;
  numero: string;
  bairro: string;
  cidade: string;
  estado: string;
  complemento: string;
  referencia: string;
  codigo_municipio: string;
  tipo_moradia: 'propria' | 'alugado' | '';
  coordenadas: string;
}

// ---------- Dados do chamado dentro do Wizard ----------
interface ChamadoWizardData {
  abrirChamado: boolean;
  incluirEnderecoAntigo: boolean;
  prioridade: 'baixa' | 'normal' | 'alta' | 'urgente';
  quemSolicitou: string;
  tecnicoResponsavel: number | null;
  dataAgendamento: Date | null;
  descricao: string;
}

// ---------- Resultado após concluir ----------
interface ResultadoMudanca {
  chamadoId?: number;
  prioridade?: string;
  dataCriacao?: string;
  dataAgendamento?: string | null;
  tecnicoResponsavel?: string | null;
}

const ContractAlteracaoWizard: React.FC<ContractAlteracaoWizardProps> = ({
  open,
  onClose,
  contract,
  onRefresh,
}) => {
  const theme = useTheme();
  const { can } = usePermissions();
  const podeEditar = can('contratos.editar');
  const podeCancelar = can('contratos.cancelar');

  const [activeStep, setActiveStep] = useState(0);
  const [acaoSelecionada, setAcaoSelecionada] = useState<AcaoWizard | null>(null);

  // ========== Alterar dia vencimento ==========
  // Step 2
  const [verificando, setVerificando] = useState(false);
  const [verificacaoOk, setVerificacaoOk] = useState(false);
  const [verificacaoErro, setVerificacaoErro] = useState<string | null>(null);
  const [faturas, setFaturas] = useState<Invoice[]>([]);

  // Step 3
  const [ultimaFaturaPaga, setUltimaFaturaPaga] = useState<Invoice | null>(null);
  const [novoDia, setNovoDia] = useState<number>(contract.dia_vencimento ?? 10);

  // Step 4
  const [gatewayId, setGatewayId] = useState<number | null>(contract.gateway_id ?? null);
  const [concluindo, setConcluindo] = useState(false);

  // ========== Mudança de endereço ==========
  const [servicoSelecionadoId, setServicoSelecionadoId] = useState<number | null>(null);
  const [servicoSelecionado, setServicoSelecionado] = useState<ContratoServicos | undefined>(undefined);

  const [enderecoForm, setEnderecoForm] = useState<EnderecoFormData>({
    cep: '', endereco: '', numero: '', bairro: '', cidade: '', estado: '', complemento: '', referencia: '', codigo_municipio: '', tipo_moradia: '', coordenadas: '',
  });

  const [updateContrato, setUpdateContrato] = useState(false);
  const [updateCliente, setUpdateCliente] = useState(false);
  const [confirmacaoSkip, setConfirmacaoSkip] = useState(false);

  // ========== Financeiro ==========
  const [cobrarTaxa, setCobrarTaxa] = useState(false);
  const [taxaValor, setTaxaValor] = useState('0,00');
  const [taxaDescricao, setTaxaDescricao] = useState('Taxa de mudança de endereço');
  const [taxaParcelar, setTaxaParcelar] = useState(false);
  const [taxaQtdeParcelas, setTaxaQtdeParcelas] = useState(1);
  const [taxaParcelas, setTaxaParcelas] = useState<{ num: number; valor: number; valorFormatado: string; vencimento: string }[]>([]);
  const [mostrarConfirmacao, setMostrarConfirmacao] = useState(false);

  const [chamadoData, setChamadoData] = useState<ChamadoWizardData>({
    abrirChamado: false,
    incluirEnderecoAntigo: true,
    prioridade: 'normal',
    quemSolicitou: '',
    tecnicoResponsavel: null,
    dataAgendamento: null,
    descricao: 'Cliente solicitou mudança de endereço',
  });
  const [selectedScheduleTime, setSelectedScheduleTime] = useState<string>('');
  const [scheduleTickets, setScheduleTickets] = useState<Ticket[]>([]);

  const [cepLoading, setCepLoading] = useState(false);
  const [cepError, setCepError] = useState<string | null>(null);
  const [bairrosOptions, setBairrosOptions] = useState<string[]>([]);
  const [loadingBairros, setLoadingBairros] = useState(false);

  const [resultadoMudanca, setResultadoMudanca] = useState<ResultadoMudanca | null>(null);

  // Estados para modal do mapa
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{ lat: number, lng: number } | null>(null);

  // Assuntos de chamado (para buscar o ID do assunto "Mudança de endereço")
  const [assuntos, setAssuntos] = useState<ChamadoAssunto[]>([]);

  // ========== Alterar Titularidade ==========
  const [novoClienteId, setNovoClienteId] = useState<number | null>(null);
  const [novoCliente, setNovoCliente] = useState<Client | null>(null);
  const [clientFormOpen, setClientFormOpen] = useState(false);
  const [migrarTitulosAbertos, setMigrarTitulosAbertos] = useState(true);
  const [migrarTitulosPagosCancelados, setMigrarTitulosPagosCancelados] = useState(false);
  const [migrarNotasNfcom, setMigrarNotasNfcom] = useState(false);
  const [migrarChamados, setMigrarChamados] = useState(false);
  const [titularidadeLoading, setTitularidadeLoading] = useState(false);
  const [titularidadeResultado, setTitularidadeResultado] = useState<any>(null);

  // ========== Cancelar contrato ==========
  const [cancelarPendLoading, setCancelarPendLoading] = useState(false);
  const [cancelarPendError, setCancelarPendError] = useState<string | null>(null);
  const [cancelarFaturas, setCancelarFaturas] = useState<Invoice[]>([]);
  const [cancelarEquipamentos, setCancelarEquipamentos] = useState<Equipment[]>([]);
  const [cancelarChamados, setCancelarChamados] = useState<Ticket[]>([]);
  const [motivoCancelamento, setMotivoCancelamento] = useState('');
  const [cancelarSubmitting, setCancelarSubmitting] = useState(false);
  const [cancelarError, setCancelarError] = useState<string | null>(null);

  // Steps dinâmicos (exibidos apenas após o step 0 de escolha)
  const wizardSteps = acaoSelecionada ? STEPS_BY_ACAO[acaoSelecionada] : [];

  // --- Carregar assuntos ---
  useEffect(() => {
    const loadAssuntos = async () => {
      try {
        const res = await chamadoAssuntoService.getAll();
        if (res.success && res.data) {
          setAssuntos(res.data);
        }
      } catch { /* ignore */ }
    };
    if (open) loadAssuntos();
  }, [open]);

  // --- Reset ao abrir ---
  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setAcaoSelecionada(null);
      setVerificando(false);
      setVerificacaoOk(false);
      setVerificacaoErro(null);
      setFaturas([]);
      setUltimaFaturaPaga(null);
      setNovoDia(contract.dia_vencimento ?? 10);
      setGatewayId(contract.gateway_id ?? null);
      setConcluindo(false);
      // Mudança de endereço
      setServicoSelecionadoId(null);
      setServicoSelecionado(undefined);
      setEnderecoForm({
        cep: '',
        endereco: '',
        numero: '',
        bairro: '',
        cidade: '',
        estado: '',
        complemento: '',
        referencia: '',
        codigo_municipio: '',
        tipo_moradia: '',
        coordenadas: '',
      });
      setUpdateContrato(false);
      setUpdateCliente(false);
      setConfirmacaoSkip(false);
      setMostrarConfirmacao(false);
      // Financeiro
      setCobrarTaxa(false);
      setTaxaValor('0,00');
      setTaxaDescricao('Taxa de mudança de endereço');
      setTaxaParcelar(false);
      setTaxaQtdeParcelas(1);
      setTaxaParcelas([]);
      setChamadoData({
        abrirChamado: false,
        incluirEnderecoAntigo: true,
        prioridade: 'normal',
        quemSolicitou: '',
        tecnicoResponsavel: null,
        dataAgendamento: null,
        descricao: 'Cliente solicitou mudança de endereço',
      });
      setSelectedScheduleTime('');
      setScheduleTickets([]);
      setCepLoading(false);
      setCepError(null);
      setResultadoMudanca(null);
      // Alterar Titularidade
      setNovoClienteId(null);
      setNovoCliente(null);
      setClientFormOpen(false);
      setMigrarTitulosAbertos(true);
      setMigrarTitulosPagosCancelados(false);
      setMigrarNotasNfcom(false);
      setMigrarChamados(false);
      setTitularidadeLoading(false);
      setTitularidadeResultado(null);
      // Cancelar contrato
      setCancelarPendLoading(false);
      setCancelarPendError(null);
      setCancelarFaturas([]);
      setCancelarEquipamentos([]);
      setCancelarChamados([]);
      setMotivoCancelamento('');
      setCancelarSubmitting(false);
      setCancelarError(null);
    }
  }, [open]);

  const handleChamadoDateChange = (date: Date | null) => {
    if (!date) {
      setSelectedScheduleTime('');
      handleChamadoChange('dataAgendamento', null);
      return;
    }

    const nextDate = new Date(date);
    if (selectedScheduleTime) {
      const [hours, minutes] = selectedScheduleTime.split(':').map(Number);
      nextDate.setHours(hours || 0, minutes || 0, 0, 0);
    }

    handleChamadoChange('dataAgendamento', nextDate);
  };

  const handleChamadoScheduleTimeChange = (time: string) => {
    setSelectedScheduleTime(time);
    if (!chamadoData.dataAgendamento) return;

    const [hours, minutes] = time.split(':').map(Number);
    const nextDate = new Date(chamadoData.dataAgendamento);
    nextDate.setHours(hours || 0, minutes || 0, 0, 0);
    handleChamadoChange('dataAgendamento', nextDate);
  };

  useEffect(() => {
    if (!chamadoData.dataAgendamento) {
      setScheduleTickets([]);
      setSelectedScheduleTime('');
      return;
    }

    const selectedDate = new Date(chamadoData.dataAgendamento);
    const selectedDateKey = toDateKey(selectedDate);

    const loadScheduleTickets = async () => {
      try {
        const response = await ticketService.getAll({
          limit: 1000,
          filters: {
            status: 'todos',
            data_inicio: selectedDateKey,
            data_fim: selectedDateKey,
          },
        });

        if (response.success && response.data) {
          setScheduleTickets(response.data);
        } else {
          setScheduleTickets([]);
        }
      } catch (error) {
        console.error('Erro ao carregar agenda do dia:', error);
        setScheduleTickets([]);
      }
    };

    loadScheduleTickets();
  }, [chamadoData.dataAgendamento]);

  useEffect(() => {
    if (!chamadoData.dataAgendamento) {
      setSelectedScheduleTime('');
      return;
    }

    const hour = String(chamadoData.dataAgendamento.getHours()).padStart(2, '0');
    const minute = String(chamadoData.dataAgendamento.getMinutes()).padStart(2, '0');
    const nextTime = `${hour}:${minute}`;

    if (nextTime !== '00:00') {
      setSelectedScheduleTime(nextTime);
    } else {
      setSelectedScheduleTime('');
    }
  }, [chamadoData.dataAgendamento]);

  const scheduleOptions = React.useMemo(() => {
    if (!chamadoData.dataAgendamento) {
      return [] as Array<{
        value: string;
        disabled: boolean;
        tickets: Array<{
          id: number;
          priority: string | undefined;
          client: string;
          tecnico?: string;
        }>;
      }>;
    }

    const selectedDate = new Date(chamadoData.dataAgendamento);
    const selectedDateKey = toDateKey(selectedDate);
    const relevantTickets = scheduleTickets.filter((ticket) => {
      if (!ticket.data_agendamento) return false;
      const ticketDate = new Date(ticket.data_agendamento);
      if (toDateKey(ticketDate) !== selectedDateKey) return false;

      if (chamadoData.tecnicoResponsavel) {
        return ticket.atribuido_para === chamadoData.tecnicoResponsavel;
      }

      return true;
    });

    const priorityOrder: Record<string, number> = {
      urgente: 4,
      alta: 3,
      normal: 2,
      baixa: 1,
    };

    const slots: Array<{
      value: string;
      disabled: boolean;
      tickets: Array<{
        id: number;
        priority: string | undefined;
        client: string;
        tecnico?: string;
      }>;
    }> = [];

    for (let hour = SCHEDULE_START_HOUR; hour <= SCHEDULE_END_HOUR; hour += 1) {
      for (let minute = 0; minute < 60; minute += SCHEDULE_STEP_MINUTES) {
        const timeValue = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        const matchingTickets = relevantTickets.filter((ticket) => {
          if (!ticket.data_agendamento) return false;
          const ticketDate = new Date(ticket.data_agendamento);
          return ticketDate.getHours() === hour && ticketDate.getMinutes() === minute;
        });

        const sortedMatchingTickets = [...matchingTickets].sort((a, b) => {
          const aPriority = priorityOrder[a.prioridade || 'normal'] ?? 0;
          const bPriority = priorityOrder[b.prioridade || 'normal'] ?? 0;

          if (bPriority !== aPriority) {
            return bPriority - aPriority;
          }

          return (a.id ?? 0) - (b.id ?? 0);
        });

        slots.push({
          value: timeValue,
          disabled: Boolean(chamadoData.tecnicoResponsavel) && matchingTickets.length > 0,
          tickets: sortedMatchingTickets.map((ticket) => ({
            id: ticket.id,
            priority: ticket.prioridade,
            client: ticket.cliente?.nome || 'Cliente',
            tecnico: ticket.atribuido_para_funcionario?.nome,
          })),
        });
      }
    }

    return slots;
  }, [chamadoData.dataAgendamento, chamadoData.tecnicoResponsavel, scheduleTickets]);

  // ---------- Funções para modal do mapa ----------
  const handleOpenMapModal = () => {
    const currentCoords = enderecoForm.coordenadas;
    if (currentCoords && currentCoords.includes(',')) {
      const [lat, lng] = currentCoords.split(',').map(coord => parseFloat(coord.trim()));
      if (!isNaN(lat) && !isNaN(lng)) {
        setSelectedLocation({ lat, lng });
      } else {
        setSelectedLocation({ lat: -14.235004, lng: -51.92528 });
      }
    } else {
      setSelectedLocation({ lat: -14.235004, lng: -51.92528 });
    }
    setMapModalOpen(true);
  };

  const handleConfirmMapLocation = (lat: number, lng: number) => {
    const coordString = `${lat.toFixed(6)},${lng.toFixed(6)}`;
    handleEnderecoChange('coordenadas', coordString);
  };

  // ========== Alterar dia vencimento - lógica ==========
  const verificarContrato = useCallback(async () => {
    setVerificando(true);
    setVerificacaoErro(null);
    setVerificacaoOk(false);

    try {
      const response = await invoiceService.getInvoicesByContract(contract.id);
      const allInvoices = response.data || [];
      setFaturas(allInvoices);

      const problemInvoices = allInvoices.filter(
        (f) =>
          f.tipo === 'mensalidade' &&
          ['vencido', 'aberto', 'parcialmente_pago'].includes(f.status)
      );

      if (problemInvoices.length > 0) {
        const statusLabels: Record<string, string> = {
          vencido: 'vencida(s)',
          aberto: 'aberta(s)',
          parcialmente_pago: 'parcialmente paga(s)',
        };
        const groups = problemInvoices.reduce((acc, f) => {
          const label = statusLabels[f.status] || f.status;
          acc[label] = (acc[label] || 0) + 1;
          return acc;
        }, {} as Record<string, number>);

        const msg = Object.entries(groups)
          .map(([label, count]) => `${count} fatura(s) ${label}`)
          .join(', ');

        setVerificacaoErro(
          `Este contrato possui ${msg}. Regularize antes de prosseguir.`
        );
        setVerificacaoOk(false);
      } else {
        setVerificacaoOk(true);

        const pagas = allInvoices
          .filter((f) => f.tipo === 'mensalidade' && f.status === 'pago')
          .sort((a, b) => {
            const dA = new Date(a.data_vencimento).getTime();
            const dB = new Date(b.data_vencimento).getTime();
            return dB - dA;
          });

        setUltimaFaturaPaga(pagas.length > 0 ? pagas[0] : null);
        setActiveStep(2);
      }
    } catch (err: any) {
      setVerificacaoErro(
        err.response?.data?.message || err.message || 'Erro ao verificar contrato'
      );
    } finally {
      setVerificando(false);
    }
  }, [contract.id]);

  const loadCancelarPendencias = useCallback(async () => {
    setCancelarPendLoading(true);
    setCancelarPendError(null);
    try {
      const [invRes, eqRes, tickRes] = await Promise.all([
        invoiceService.getInvoicesByContract(contract.id),
        equipmentService.getEquipmentsByContract(contract.id),
        ticketService.getByClientId(contract.cliente_id, { page: 1, limit: 500 }),
      ]);

      const allInv = (invRes.success && invRes.data ? invRes.data : []) as Invoice[];
      setCancelarFaturas(allInv.filter(isFaturaPendenteCancelamento));

      const allEq = (eqRes.success && eqRes.data ? eqRes.data : []) as Equipment[];
      setCancelarEquipamentos(allEq);

      const allTickets = (tickRes.success && tickRes.data ? tickRes.data : []) as Ticket[];
      const contractTickets = allTickets.filter((t) => t.contrato_id === contract.id);
      setCancelarChamados(contractTickets.filter(isChamadoPendenteWizard));
    } catch (err: any) {
      setCancelarPendError(
        err.response?.data?.message || err.message || 'Erro ao carregar pendências'
      );
    } finally {
      setCancelarPendLoading(false);
    }
  }, [contract.id, contract.cliente_id]);

  useEffect(() => {
    if (open && acaoSelecionada === 'cancelar_contrato' && activeStep === 1) {
      loadCancelarPendencias();
    }
  }, [open, acaoSelecionada, activeStep, loadCancelarPendencias]);

  useEffect(() => {
    if (activeStep === 1 && acaoSelecionada === 'alterar_dia_vencimento') {
      verificarContrato();
    }
  }, [activeStep, acaoSelecionada, verificarContrato]);

  const calcProporcional = useCallback(() => {
    if (!ultimaFaturaPaga) return null;

    const modalidade = contract.modalidade || 'pos-pago';
    const valorContrato = contract.valor_contratado ?? 0;

    const dataFimUltima = ultimaFaturaPaga.data_final
      ? new Date(ultimaFaturaPaga.data_final + 'T12:00:00')
      : null;

    if (!dataFimUltima) return null;

    const proporcionalStart = addDays(dataFimUltima, 1);
    const proximaMensalidade = calcProximaMensalidade(novoDia);

    let proporcionalEnd: Date;
    if (modalidade === 'pos-pago') {
      proporcionalEnd = addDays(proximaMensalidade, -1);
    } else {
      const nextMonth = proximaMensalidade.getMonth() + 2;
      const nextYear = proximaMensalidade.getFullYear() + (nextMonth > 12 ? 1 : 0);
      const m = nextMonth > 12 ? nextMonth - 12 : nextMonth;
      const nextCycleDate = buildDateWithDay(novoDia, nextYear, m);
      proporcionalEnd = addDays(nextCycleDate, -1);
    }

    const diasProporcional = diffDays(proporcionalStart, proporcionalEnd);
    const valorDiario = valorContrato / 30;
    const valorProporcional = Math.round(valorDiario * diasProporcional * 100) / 100;

    const mesRef = `${proporcionalStart.getFullYear()}-${String(proporcionalStart.getMonth() + 1).padStart(2, '0')}`;

    return {
      dataInicio: toDateOnly(proporcionalStart),
      dataFim: toDateOnly(proporcionalEnd),
      dias: diasProporcional,
      valor: valorProporcional,
      valorDiario: Math.round(valorDiario * 100) / 100,
      vencimento: toDateOnly(proximaMensalidade),
      pagoAte: toDateOnly(dataFimUltima),
      mesReferencia: mesRef,
      descricao: `Mensalidade (${formatDate(toDateOnly(proporcionalStart))} a ${formatDate(toDateOnly(proporcionalEnd))})`,
    };
  }, [ultimaFaturaPaga, novoDia, contract]);

  const handleConcluirDiaVencimento = async () => {
    setConcluindo(true);
    const prop = calcProporcional();

    try {
      await contractService.update(contract.id, {
        dia_vencimento: novoDia,
      });

      if (prop) {
        const faturaData: InvoiceFormData = {
          cliente_id: contract.cliente_id,
          contrato_id: contract.id,
          data_vencimento: prop.vencimento,
          tipo: 'mensalidade',
          valor: prop.valor,
          descricao: prop.descricao,
          mes_referencia: prop.mesReferencia,
          data_inicial: prop.dataInicio,
          data_final: prop.dataFim,
          gateway_id: gatewayId,
        };

        await invoiceService.createInvoice(faturaData);
      }

      if (prop) {
        toast.success('Dia de vencimento alterado e fatura proporcional criada com sucesso!');
      } else {
        toast.success('Dia de vencimento alterado com sucesso!');
      }
      onClose();
      onRefresh();
    } catch (err: any) {
      const msg =
        err.response?.data?.message || err.message || 'Erro ao concluir alteração';
      toast.error(msg);
    } finally {
      setConcluindo(false);
    }
  };

  // ========== Mudança de endereço - lógica ==========
  const handleEnderecoChange = (field: keyof EnderecoFormData, value: string) => {
    setEnderecoForm((prev) => ({ ...prev, [field]: value }));
    // Resetar confirmação ao alterar campos
    setConfirmacaoSkip(false);
    setMostrarConfirmacao(false);
  };

  const preencherEnderecoDoServico = () => {
    if (!servicoSelecionado) return;
    setEnderecoForm({
      cep: servicoSelecionado.cep_inst || '',
      endereco: servicoSelecionado.endereco_inst || '',
      numero: servicoSelecionado.numero_inst || '',
      bairro: servicoSelecionado.bairro_inst || '',
      cidade: servicoSelecionado.cidade_inst || '',
      estado: servicoSelecionado.estado_inst || '',
      complemento: servicoSelecionado.complemento_inst || '',
      referencia: servicoSelecionado.referencia_inst || '',
      codigo_municipio: servicoSelecionado.codigo_municipio || '',
      tipo_moradia: servicoSelecionado.tipo_moradia || '',
      coordenadas: servicoSelecionado.coordenadas_inst || '',
    });
    if (servicoSelecionado.cidade_inst) {
      loadBairrosForCidade(servicoSelecionado.cidade_inst);
    }
    setConfirmacaoSkip(false);
    setMostrarConfirmacao(false);
  };

  const preencherEnderecoDoContrato = () => {
    setEnderecoForm({
      cep: contract.cep_inst || '',
      endereco: contract.endereco_inst || '',
      numero: contract.numero_inst || '',
      bairro: contract.bairro_inst || '',
      cidade: contract.cidade_inst || '',
      estado: contract.estado_inst || '',
      complemento: contract.complemento_inst || '',
      referencia: contract.referencia_inst || '',
      codigo_municipio: contract.codigo_municipio || '',
      tipo_moradia: contract.tipo_moradia || '',
      coordenadas: '',
    });
    if (contract.cidade_inst) {
      loadBairrosForCidade(contract.cidade_inst);
    }
    setConfirmacaoSkip(false);
    setMostrarConfirmacao(false);
  };

  const preencherEnderecoDoCliente = () => {
    if (!contract.cliente) return;
    setEnderecoForm({
      cep: contract.cliente.cep || '',
      endereco: contract.cliente.endereco || '',
      numero: contract.cliente.numero || '',
      bairro: contract.cliente.bairro || '',
      cidade: contract.cliente.cidade || '',
      estado: contract.cliente.estado || '',
      complemento: contract.cliente.complemento || '',
      referencia: contract.cliente.referencia || '',
      codigo_municipio: contract.cliente.codigo_municipio || '',
      tipo_moradia: contract.cliente.tipo_moradia || '',
      coordenadas: '',
    });
    if (contract.cliente.cidade) {
      loadBairrosForCidade(contract.cliente.cidade);
    }
    setConfirmacaoSkip(false);
    setMostrarConfirmacao(false);
  };

  const handleChamadoChange = (field: keyof ChamadoWizardData, value: any) => {
    setChamadoData((prev) => ({ ...prev, [field]: value }));
  };

  const loadBairrosForCidade = (cidade: string) => {
    const c = (cidade || '').trim();
    if (!c) {
      setBairrosOptions([]);
      return;
    }
    setLoadingBairros(true);
    clientService
      .getBairrosByCidade(c)
      .then(setBairrosOptions)
      .finally(() => setLoadingBairros(false));
  };

  const handleBuscarCep = async () => {
    const cep = enderecoForm.cep;
    if (!cep || cep.length < 8) {
      setCepError('Digite um CEP válido');
      return;
    }

    setCepLoading(true);
    setCepError(null);

    try {
      const cepNumeros = cep.replace(/\D/g, '');
      const response = await fetch(`https://viacep.com.br/ws/${cepNumeros}/json/`);
      const data = await response.json();

      if (data.erro) {
        setCepError('CEP não encontrado');
        return;
      }

      setEnderecoForm((prev) => ({
        ...prev,
        endereco: data.logradouro || prev.endereco,
        bairro: data.bairro || prev.bairro,
        cidade: data.localidade || prev.cidade,
        estado: data.uf || prev.estado,
        codigo_municipio: data.ibge || prev.codigo_municipio,
      }));

      if (data.localidade) {
        loadBairrosForCidade(data.localidade);
      }
    } catch {
      setCepError('Erro ao buscar CEP');
    } finally {
      setCepLoading(false);
    }
  };

  const getEnderecoAntigoServico = (): string => {
    if (!servicoSelecionado) return '';
    const parts = [
      servicoSelecionado.endereco_inst && `Rua: ${servicoSelecionado.endereco_inst} ${servicoSelecionado.numero_inst && `- ${servicoSelecionado.numero_inst}`}`,
      servicoSelecionado.bairro_inst && `Bairro: ${servicoSelecionado.bairro_inst}`,
      servicoSelecionado.cidade_inst && `Cidade: ${servicoSelecionado.cidade_inst} - ${servicoSelecionado.estado_inst}`,
      servicoSelecionado.complemento_inst && `Complemento: ${servicoSelecionado.complemento_inst}`,
      servicoSelecionado.referencia_inst && `Referência: ${servicoSelecionado.referencia_inst}`,
      servicoSelecionado.coordenadas_inst && `Coordenadas: ${servicoSelecionado.coordenadas_inst}`
    ].filter(Boolean);
    return parts.join('\n');
  };

  const handleConcluirMudancaEndereco = async () => {
    setConcluindo(true);

    try {
      const enderecoData = {
        cep_inst: enderecoForm.cep,
        endereco_inst: enderecoForm.endereco,
        numero_inst: enderecoForm.numero,
        bairro_inst: enderecoForm.bairro,
        cidade_inst: enderecoForm.cidade,
        estado_inst: enderecoForm.estado,
        complemento_inst: enderecoForm.complemento,
        referencia_inst: enderecoForm.referencia,
        codigo_municipio: enderecoForm.codigo_municipio,
        tipo_moradia: enderecoForm.tipo_moradia || undefined,
        coordenadas_inst: enderecoForm.coordenadas,
      };

      // 1) Atualizar endereço do serviço
      if (servicoSelecionadoId) {
        await ContratoServicosService.update(servicoSelecionadoId, enderecoData);
      }

      // 2) Se contrato marcado, atualizar endereço do contrato
      if (updateContrato) {
        await contractService.update(contract.id, enderecoData);
      }

      // 3) Se cliente marcado, atualizar endereço do cliente
      if (updateCliente) {
        await clientService.update(contract.cliente_id, {
          cep: enderecoForm.cep,
          endereco: enderecoForm.endereco,
          numero: enderecoForm.numero,
          bairro: enderecoForm.bairro,
          cidade: enderecoForm.cidade,
          estado: enderecoForm.estado,
          complemento: enderecoForm.complemento,
          referencia: enderecoForm.referencia,
          codigo_municipio: enderecoForm.codigo_municipio,
          tipo_moradia: enderecoForm.tipo_moradia || undefined,
        });
      }
      // 4) Se cobrar taxa de serviço, criar fatura(s)
      if (cobrarTaxa) {
        const valorTotal = parsearMoeda(taxaValor);
        if (valorTotal > 0) {
          if (taxaParcelar && taxaParcelas.length > 1) {
            // Criar uma fatura por parcela
            for (const parcela of taxaParcelas) {
              const faturaData: InvoiceFormData = {
                cliente_id: contract.cliente_id,
                contrato_id: contract.id,
                data_vencimento: parcela.vencimento,
                tipo: 'servicos',
                valor: parcela.valor,
                descricao: `${taxaDescricao} (${parcela.num}/${taxaParcelas.length})`,
                gateway_id: gatewayId || undefined,
              };
              await invoiceService.createInvoice(faturaData);
            }
          } else {
            // Fatura única
            const diaVenc = contract.dia_vencimento || 10;
            const dataVenc = getProximoVencimentoTaxa(diaVenc);
            const vencimento = toDateOnly(dataVenc);

            const faturaData: InvoiceFormData = {
              cliente_id: contract.cliente_id,
              contrato_id: contract.id,
              data_vencimento: vencimento,
              tipo: 'servicos',
              valor: valorTotal,
              descricao: taxaDescricao,
              mes_referencia: `${String(dataVenc.getMonth() + 1).padStart(2, '0')}-${dataVenc.getFullYear()}`,
              gateway_id: gatewayId || undefined,
            };
            await invoiceService.createInvoice(faturaData);
          }
        }
      }

      // 5) Se abrir chamado
      let resultado: ResultadoMudanca = {};
      if (chamadoData.abrirChamado) {
        if (chamadoData.dataAgendamento && chamadoData.tecnicoResponsavel) {
          const selectedDate = new Date(chamadoData.dataAgendamento);
          const selectedDateKey = toDateKey(selectedDate);
          const hasConflict = scheduleTickets.some((ticket) => {
            if (!ticket.data_agendamento) return false;
            const ticketDate = new Date(ticket.data_agendamento);
            if (toDateKey(ticketDate) !== selectedDateKey) return false;
            if (ticket.atribuido_para !== chamadoData.tecnicoResponsavel) return false;
            return ticketDate.getHours() === selectedDate.getHours() && ticketDate.getMinutes() === selectedDate.getMinutes();
          });

          if (hasConflict) {
            const hora = formatHourLabel(selectedDate);
            toast.error(`O técnico já tem outro chamado agendado para ${selectedDate.toLocaleDateString('pt-BR')} às ${hora}. Escolha outro horário.`);
            setConcluindo(false);
            return;
          }
        }

        // Buscar assunto "Mudança de endereço"
        const assuntoMudanca = assuntos.find(
          (a) => a.nome.toLowerCase().includes('mudança de endereço') ||
            a.nome.toLowerCase().includes('mudanca de endereco')
        );

        let descricaoFinal = chamadoData.descricao;
        if (chamadoData.incluirEnderecoAntigo) {
          const enderecoAntigo = getEnderecoAntigoServico();
          if (enderecoAntigo) {
            descricaoFinal += `\n\nEndereço anterior:\n${enderecoAntigo}`;
          }
        }

        const ticketData = {
          cliente_id: contract.cliente_id,
          contrato_id: contract.id,
          contrato_servico_id: servicoSelecionadoId || undefined,
          chamado_assunto_id: assuntoMudanca?.id || undefined,
          descricao: descricaoFinal,
          prioridade: chamadoData.prioridade,
          quem_solicitou: chamadoData.quemSolicitou || undefined,
          data_agendamento: chamadoData.dataAgendamento || undefined,
          atribuido_para: chamadoData.tecnicoResponsavel || undefined,
        };

        const ticketResponse = await ticketService.create(ticketData);

        if (ticketResponse.success && ticketResponse.data) {
          resultado = {
            chamadoId: ticketResponse.data.id,
            prioridade: chamadoData.prioridade,
            dataCriacao: ticketResponse.data.createdAt,
            dataAgendamento: chamadoData.dataAgendamento
              ? chamadoData.dataAgendamento.toISOString()
              : null,
            tecnicoResponsavel: ticketResponse.data.atribuido_para_funcionario?.nome || null,
          };
        }
      }

      setResultadoMudanca(resultado);
      toast.success('Mudança de endereço realizada com sucesso!');
      onClose();
      onRefresh();
      // Avançar para etapa final (step 0 é escolha, então último step = wizardSteps.length)
      setActiveStep(wizardSteps.length);
    } catch (err: any) {
      const msg =
        err.response?.data?.message || err.message || 'Erro ao concluir mudança de endereço';
      toast.error(msg);
    } finally {
      setConcluindo(false);
    }
  };

  // ========== Navigation ==========
  const canAdvance = (): boolean => {
    if (activeStep === 0) return acaoSelecionada !== null;

    if (acaoSelecionada === 'alterar_dia_vencimento') {
      switch (activeStep) {
        case 1: return verificacaoOk;
        case 2: return novoDia !== contract.dia_vencimento;
        case 3: return true;
        default: return false;
      }
    }

    if (acaoSelecionada === 'mudanca_endereco') {
      switch (activeStep) {
        case 1: // Serviço
          return servicoSelecionadoId !== null;
        case 2: // Dados Novo Endereço
          {
            const camposOk = !!(
              enderecoForm.cep &&
              enderecoForm.endereco &&
              enderecoForm.numero &&
              enderecoForm.bairro &&
              enderecoForm.cidade &&
              enderecoForm.estado
            );
            if (!camposOk) return false;
            // Se algum checkbox desmarcado, precisa de confirmação
            if ((!updateContrato || !updateCliente) && mostrarConfirmacao) {
              return confirmacaoSkip;
            }
            return true;
          }
        case 3: // Financeiro
          {
            if (!cobrarTaxa) return true;
            const valorTaxa = parsearMoeda(taxaValor);
            return valorTaxa > 0 && taxaDescricao.trim().length > 0 && gatewayId !== null;
          }
        case 4: // Chamado
          return true;
        case 5: // Finalizado
          return true;
        default:
          return false;
      }
    }

    if (acaoSelecionada === 'alterar_titularidade') {
      switch (activeStep) {
        case 1: // Novo Titular
          return novoClienteId !== null && novoClienteId !== contract.cliente_id;
        case 2: // Migração de Dados
          return true;
        case 3: // Confirmação
          return true;
        default:
          return false;
      }
    }

    if (acaoSelecionada === 'cancelar_contrato') {
      if (activeStep === 1) return true;
      if (activeStep === 2) return motivoCancelamento.trim().length > 0;
      return false;
    }

    return false;
  };

  // ========== Alterar Titularidade - Concluir ==========
  const handleConcluirTitularidade = async () => {
    if (!novoClienteId) return;
    setTitularidadeLoading(true);
    try {
      const response = await contractService.alterarTitularidade(contract.id, {
        novo_cliente_id: novoClienteId,
        migrar_titulos_abertos: migrarTitulosAbertos,
        migrar_titulos_pagos_cancelados: migrarTitulosPagosCancelados,
        migrar_notas_nfcom: migrarNotasNfcom,
        migrar_chamados: migrarChamados,
      });

      if (response.success) {
        setTitularidadeResultado((response as any).resultado);
        toast.success(response.message || 'Titularidade alterada com sucesso!');
        onRefresh();
        onClose();
      } else {
        toast.error(response.message || 'Erro ao alterar titularidade');
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Erro ao alterar titularidade';
      toast.error(msg);
    } finally {
      setTitularidadeLoading(false);
    }
  };

  const handleConfirmCancelarContrato = async () => {
    const m = motivoCancelamento.trim();
    if (!m) {
      toast.error('Informe o motivo do cancelamento.');
      return;
    }
    setCancelarSubmitting(true);
    setCancelarError(null);
    try {
      const response = await contractService.cancelar(contract.id, m);
      if (response.success) {
        toast.success(response.message || 'Contrato cancelado com sucesso!');
        onRefresh();
        onClose();
      } else {
        setCancelarError(response.message || 'Erro ao cancelar contrato');
      }
    } catch (err: any) {
      setCancelarError(err.response?.data?.message || err.message || 'Erro ao cancelar contrato');
    } finally {
      setCancelarSubmitting(false);
    }
  };

  const handleNext = () => {
    // Alterar titularidade: Step 3 (Confirmação) -> Concluir
    if (acaoSelecionada === 'alterar_titularidade' && activeStep === 3) {
      handleConcluirTitularidade();
      return;
    }

    // Mudança de endereço: Step 2 (endereço) - validar checkboxes
    if (acaoSelecionada === 'mudanca_endereco' && activeStep === 2) {
      if (!updateContrato || !updateCliente) {
        if (!mostrarConfirmacao) {
          setMostrarConfirmacao(true);
          return;
        }
        if (!confirmacaoSkip) {
          return;
        }
      }
      // Reset para próxima vez
      setMostrarConfirmacao(false);
      setConfirmacaoSkip(false);
    }

    // Alterar dia vencimento: Step 1 (verificação) avança automaticamente
    if (acaoSelecionada === 'alterar_dia_vencimento' && activeStep === 1 && verificacaoOk) {
      setActiveStep(2);
      return;
    }

    // Mudança de endereço: Step 4 (Chamado) -> Step 5 (Finalizado) executa ação antes
    if (acaoSelecionada === 'mudanca_endereco' && activeStep === 4) {
      handleConcluirMudancaEndereco();
      return;
    }

    if (activeStep < wizardSteps.length) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) setActiveStep((prev) => prev - 1);
  };

  const isLastStep = activeStep === wizardSteps.length;
  const isFinalizado = acaoSelecionada === 'mudanca_endereco' && activeStep === wizardSteps.length;

  // ========== RENDER: Step 0 - Escolha ação ==========
  const renderStep0 = () => {
    const isSuspenso = contract.status === 'suspenso';

    return (
      <Box sx={{ py: 1 }}>
        {isSuspenso && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            Este contrato está <strong>suspenso</strong>. Apenas a opção de <strong>cancelar contrato</strong> está disponível no momento.
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Selecione a ação que deseja realizar neste contrato:
        </Typography>

        {podeEditar && (
          <Card
            variant="outlined"
            onClick={() => {
              if (!isSuspenso) {
                setAcaoSelecionada('alterar_dia_vencimento');
                setActiveStep(1);
              }
            }}
            sx={{
              cursor: isSuspenso ? 'not-allowed' : 'pointer',
              opacity: isSuspenso ? 0.4 : 1,
              mb: 1,
              borderColor:
                !isSuspenso && acaoSelecionada === 'alterar_dia_vencimento'
                  ? theme.palette.primary.main
                  : undefined,
              bgcolor:
                isSuspenso
                  ? alpha(theme.palette.action.disabledBackground, 0.4)
                  : acaoSelecionada === 'alterar_dia_vencimento'
                    ? alpha(theme.palette.primary.main, 0.04)
                    : undefined,
              transition: 'all 0.2s',
              ...(!isSuspenso && {
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  bgcolor: alpha(theme.palette.primary.main, 0.02),
                },
              }),
            }}
          >
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <CalendarMonth
                color={isSuspenso ? 'disabled' : (acaoSelecionada === 'alterar_dia_vencimento' ? 'primary' : 'action')}
                sx={{ fontSize: 32 }}
              />
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold" color={isSuspenso ? 'text.disabled' : 'text.primary'}>
                    Alterar dia de vencimento
                  </Typography>
                </Box>
                <Typography variant="caption" color={isSuspenso ? 'text.disabled' : 'text.secondary'}>
                  Altera o dia de vencimento e gera fatura proporcional.
                </Typography>
              </Box>
              {!isSuspenso && acaoSelecionada === 'alterar_dia_vencimento' && (
                <CheckCircle color="primary" fontSize="small" />
              )}
            </CardContent>
          </Card>
        )}

        {podeEditar && (
          <Card
            variant="outlined"
            onClick={() => {
              if (!isSuspenso) {
                setAcaoSelecionada('alterar_titularidade');
                setActiveStep(1);
              }
            }}
            sx={{
              cursor: isSuspenso ? 'not-allowed' : 'pointer',
              opacity: isSuspenso ? 0.4 : 1,
              mt: 1,
              borderColor:
                !isSuspenso && acaoSelecionada === 'alterar_titularidade'
                  ? theme.palette.primary.main
                  : undefined,
              bgcolor:
                isSuspenso
                  ? alpha(theme.palette.action.disabledBackground, 0.4)
                  : acaoSelecionada === 'alterar_titularidade'
                    ? alpha(theme.palette.primary.main, 0.04)
                    : undefined,
              transition: 'all 0.2s',
              ...(!isSuspenso && {
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  bgcolor: alpha(theme.palette.primary.main, 0.02),
                },
              }),
            }}
          >
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <SwapHoriz
                color={isSuspenso ? 'disabled' : (acaoSelecionada === 'alterar_titularidade' ? 'primary' : 'action')}
                sx={{ fontSize: 32 }}
              />
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold" color={isSuspenso ? 'text.disabled' : 'text.primary'}>
                    Alterar Titularidade
                  </Typography>
                </Box>
                <Typography variant="caption" color={isSuspenso ? 'text.disabled' : 'text.secondary'}>
                  Transfere o contrato para outro cliente e migra dados relacionados.
                </Typography>
              </Box>
              {!isSuspenso && acaoSelecionada === 'alterar_titularidade' && (
                <CheckCircle color="primary" fontSize="small" />
              )}
            </CardContent>
          </Card>
        )}

        {podeEditar && (
          <Card
            variant="outlined"
            onClick={() => {
              if (!isSuspenso) {
                setAcaoSelecionada('mudanca_endereco');
                setActiveStep(1);
              }
            }}
            sx={{
              cursor: isSuspenso ? 'not-allowed' : 'pointer',
              opacity: isSuspenso ? 0.4 : 1,
              mt: 1,
              borderColor:
                !isSuspenso && acaoSelecionada === 'mudanca_endereco'
                  ? theme.palette.primary.main
                  : undefined,
              bgcolor:
                isSuspenso
                  ? alpha(theme.palette.action.disabledBackground, 0.4)
                  : acaoSelecionada === 'mudanca_endereco'
                    ? alpha(theme.palette.primary.main, 0.04)
                    : undefined,
              transition: 'all 0.2s',
              ...(!isSuspenso && {
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  bgcolor: alpha(theme.palette.primary.main, 0.02),
                },
              }),
            }}
          >
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <LocationOn
                color={isSuspenso ? 'disabled' : (acaoSelecionada === 'mudanca_endereco' ? 'primary' : 'action')}
                sx={{ fontSize: 32 }}
              />
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold" color={isSuspenso ? 'text.disabled' : 'text.primary'}>
                    Mudança de Endereço
                  </Typography>
                </Box>
                <Typography variant="caption" color={isSuspenso ? 'text.disabled' : 'text.secondary'}>
                  Atualiza o endereço do serviço, contrato e/ou cliente.
                </Typography>
              </Box>
              {!isSuspenso && acaoSelecionada === 'mudanca_endereco' && (
                <CheckCircle color="primary" fontSize="small" />
              )}
            </CardContent>
          </Card>
        )}

        {podeEditar && podeCancelar && <Divider sx={{ my: 2 }} />}

        {podeCancelar && contract.status !== 'cancelado' && (
          <Card
            variant="outlined"
            onClick={() => {
              setAcaoSelecionada('cancelar_contrato');
              setActiveStep(1);
            }}
            sx={{
              cursor: 'pointer',
              mt: 1,
              borderColor:
                acaoSelecionada === 'cancelar_contrato'
                  ? theme.palette.error.main
                  : undefined,
              bgcolor:
                acaoSelecionada === 'cancelar_contrato'
                  ? alpha(theme.palette.error.main, 0.06)
                  : undefined,
              transition: 'all 0.2s',
              '&:hover': {
                borderColor: theme.palette.error.main,
                bgcolor: alpha(theme.palette.error.main, 0.04),
              },
            }}
          >
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Block
                color={acaoSelecionada === 'cancelar_contrato' ? 'error' : 'action'}
                sx={{ fontSize: 32 }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Cancelar contrato
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Consulta pendências (faturas, equipamentos e chamados) e permite cancelar o contrato.
                </Typography>
              </Box>
              {acaoSelecionada === 'cancelar_contrato' && (
                <CheckCircle color="error" fontSize="small" />
              )}
            </CardContent>
          </Card>
        )}
      </Box>
    );
  };
  // ========== Alterar dia vencimento - Steps ==========
  const renderDiaVencimentoStep1 = () => (
    <Box sx={{ py: 1 }}>
      {verificando && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 3 }}>
          <CircularProgress size={28} />
          <Typography variant="body2" color="text.secondary">
            Verificando situação do contrato...
          </Typography>
        </Box>
      )}

      {!verificando && verificacaoErro && (
        <Alert severity="error" icon={<Warning fontSize="small" />}>
          {verificacaoErro}
        </Alert>
      )}

      {!verificando && verificacaoOk && (
        <Alert severity="success" icon={<CheckCircle fontSize="small" />}>
          Contrato verificado! Nenhuma fatura pendente encontrada.
        </Alert>
      )}
    </Box>
  );

  const renderDiaVencimentoStep2 = () => {
    const modalidadeLabel = contract.modalidade === 'pre-pago' ? 'Pré-pago' : 'Pós-pago';

    return (
      <Box sx={{ py: 1 }}>
        <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
          <Grid item xs={12} sm={6}>
            <Card variant="outlined">
              <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold">
                  Contrato
                </Typography>
                <Divider sx={{ my: 0.5 }} />
                <InfoRow label="Dia Vencimento" value={`Dia ${String(contract.dia_vencimento).padStart(2, '0')}`} />
                <InfoRow label="Modalidade" value={modalidadeLabel} />
                <InfoRow label="Valor" value={formatCurrency(contract.valor_contratado ?? 0)} />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Card variant="outlined">
              <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold">
                  Última Fatura Paga (Mensalidade)
                </Typography>
                <Divider sx={{ my: 0.5 }} />
                {ultimaFaturaPaga ? (
                  <>
                    <InfoRow label="Vencimento" value={formatDate(ultimaFaturaPaga.data_vencimento)} />
                    <InfoRow label="Valor" value={formatCurrency(ultimaFaturaPaga.valor)} />
                    <InfoRow label="Período" value={`${formatDate(ultimaFaturaPaga.data_inicial)} até ${formatDate(ultimaFaturaPaga.data_final)}`} />
                  </>
                ) : (
                  <Alert severity="warning" sx={{ mt: 0.5, py: 0 }}>
                    Nenhuma fatura paga encontrada.
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Card variant="outlined" sx={{ borderColor: theme.palette.primary.main }}>
          <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
            <Typography variant="caption" color="primary" fontWeight="bold">
              Novo Dia de Vencimento
            </Typography>
            <Divider sx={{ my: 0.5 }} />
            <Box sx={{ maxWidth: 220, mt: 0.5 }}>
              <DiasVencimentoSelect
                value={novoDia}
                onChange={(v) => {
                  if (v !== '') setNovoDia(v as number);
                }}
                required
              />
            </Box>
            {novoDia === contract.dia_vencimento && (
              <Alert severity="info" sx={{ mt: 1, py: 0 }}>
                Selecione um dia diferente do atual para prosseguir.
              </Alert>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  };

  const renderDiaVencimentoStep3 = () => {
    const prop = calcProporcional();
    const modalidadeLabel = contract.modalidade === 'pre-pago' ? 'Pré-pago' : 'Pós-pago';

    return (
      <Box sx={{ py: 1 }}>
        {prop && (
          <>
            <Alert severity="info" icon={<Info fontSize="small" />} sx={{ mb: 1.5, py: 0.5 }}>
              <Typography variant="caption">
                Pago até <strong>{formatDate(prop.pagoAte)}</strong>.
                Alteração dia <strong>{String(contract.dia_vencimento).padStart(2, '0')}</strong> → <strong>{String(novoDia).padStart(2, '0')}</strong> ({modalidadeLabel}) gerou diferença de <strong>{prop.dias} dia(s)</strong>.
              </Typography>
            </Alert>

            <Card variant="outlined" sx={{ mb: 1.5 }}>
              <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                <Typography variant="caption" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Receipt fontSize="small" />
                  Fatura Proporcional
                </Typography>
                <Divider sx={{ my: 0.5 }} />
                <InfoRow label="Vencimento" value={formatDate(prop.vencimento)} />
                <InfoRow label="Valor" value={formatCurrency(prop.valor)} highlight />
                <InfoRow label="Período" value={`${formatDate(prop.dataInicio)} até ${formatDate(prop.dataFim)}`} />
                <InfoRow label="Dias" value={`${prop.dias} dia(s) × ${formatCurrency(prop.valorDiario)}/dia`} />
                <InfoRow label="Descrição" value={prop.descricao} />
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                <Typography variant="caption" fontWeight="bold" sx={{ mb: 0.5, display: 'block' }}>
                  Gateway de Pagamento
                </Typography>
                <GatewaySelect
                  value={gatewayId ?? undefined}
                  onChange={(id) => setGatewayId(id)}
                  label="Gateway de Pagamento"
                  showOnlyActive
                  size="small"
                />
              </CardContent>
            </Card>
          </>
        )}

        {!prop && (
          <Alert severity="info" sx={{ py: 1 }}>
            Nenhuma fatura paga encontrada para o cálculo proporcional. Ao concluir, o dia de vencimento do contrato será atualizado para o <strong>dia {String(novoDia).padStart(2, '0')}</strong> sem a criação de fatura proporcional.
          </Alert>
        )}
      </Box>
    );
  };

  // ========== Mudança de endereço - Steps ==========
  const renderEnderecoStep1Servico = () => (
    <Box sx={{ py: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Selecione o serviço que terá o endereço alterado:
      </Typography>
      <ContractServicosSelect
        value={servicoSelecionadoId}
        onChange={(servicoId, servico) => {
          setServicoSelecionadoId(servicoId);
          setServicoSelecionado(servico);
          if (contract.gateway_id) setGatewayId(contract.gateway_id);
        }}
        contractId={contract.id}
        label="Serviço"
        required
        size="small"
        onlyActive={false}
        showContractTitle={false}
      />

      {servicoSelecionado && (
        <Card variant="outlined" sx={{ mt: 1.5 }}>
          <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
            <Typography variant="caption" color="text.secondary" fontWeight="bold">
              Endereço Atual do Serviço
            </Typography>
            <Divider sx={{ my: 0.5 }} />
            <InfoRow label="Endereço" value={servicoSelecionado.endereco_inst || '—'} />
            <InfoRow label="Número" value={servicoSelecionado.numero_inst || '—'} />
            <InfoRow label="Bairro" value={servicoSelecionado.bairro_inst || '—'} />
            <InfoRow label="Cidade" value={servicoSelecionado.cidade_inst || '—'} />
            <InfoRow label="Estado" value={servicoSelecionado.estado_inst || '—'} />
            <InfoRow label="CEP" value={servicoSelecionado.cep_inst || '—'} />
            {servicoSelecionado.complemento_inst && (
              <InfoRow label="Complemento" value={servicoSelecionado.complemento_inst} />
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );

  const renderEnderecoStep2DadosEndereco = () => (
    <Box sx={{ py: 1 }}>
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Informe os dados do novo endereço:
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Search />}
            onClick={preencherEnderecoDoServico}
            disabled={!servicoSelecionadoId}
          >
            Buscar do Serviço
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Search />}
            onClick={preencherEnderecoDoContrato}
          >
            Buscar do Contrato
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Search />}
            onClick={preencherEnderecoDoCliente}
            disabled={!contract.cliente}
          >
            Buscar do Cliente
          </Button>
        </Box>
      </Box>

      {cepError && (
        <Alert severity="error" sx={{ mb: 1, py: 0 }}>
          {cepError}
        </Alert>
      )}

      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={2.5}>
          <TextField
            fullWidth
            size="small"
            label="CEP *"
            value={enderecoForm.cep}
            onChange={(e) => handleEnderecoChange('cep', formatarCepOnInput(e.target.value))}
            onKeyDown={(e) => { if (e.key === 'Enter') handleBuscarCep(); }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Buscar CEP">
                    <IconButton
                      onClick={handleBuscarCep}
                      disabled={cepLoading || !enderecoForm.cep || enderecoForm.cep.length < 8}
                      size="small"
                      sx={{ p: 0.5 }}
                    >
                      {cepLoading ? <CircularProgress size={16} /> : <Search sx={{ fontSize: '1rem' }} />}
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} sm={5}>
          <TextField
            fullWidth
            size="small"
            label="Cidade *"
            value={enderecoForm.cidade}
            onChange={(e) => handleEnderecoChange('cidade', e.target.value)}
            onBlur={() => loadBairrosForCidade(enderecoForm.cidade)}
          />
        </Grid>

        <Grid item xs={12} sm={2.5}>
          <EstadosBrSelect
            value={enderecoForm.estado}
            onChange={(e) => handleEnderecoChange('estado', e.target.value)}
            label="Estado *"
            required
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            size="small"
            label="Código Município *"
            value={enderecoForm.codigo_municipio}
            onChange={(e) => handleEnderecoChange('codigo_municipio', e.target.value)}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <Autocomplete
            freeSolo
            options={bairrosOptions}
            value={enderecoForm.bairro}
            onInputChange={(_, value) => handleEnderecoChange('bairro', value || '')}
            onChange={(_, value) => handleEnderecoChange('bairro', typeof value === 'string' ? value : value || '')}
            loading={loadingBairros}
            disabled={!enderecoForm.cidade?.trim()}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Bairro *"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingBairros ? <CircularProgress color="inherit" size={20} sx={{ mr: 1 }} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label="Endereço *"
            value={enderecoForm.endereco}
            onChange={(e) => handleEnderecoChange('endereco', e.target.value)}
          />
        </Grid>

        <Grid item xs={12} sm={2}>
          <TextField
            fullWidth
            size="small"
            label="Número *"
            value={enderecoForm.numero}
            onChange={(e) => handleEnderecoChange('numero', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={7}>
          <TextField
            fullWidth
            size="small"
            label="Referência"
            value={enderecoForm.referencia}
            onChange={(e) => handleEnderecoChange('referencia', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={5}>
          <TextField
            fullWidth
            size="small"
            label="Coordenadas (lat,lng)"
            placeholder="-23.5505,-46.6333"
            value={enderecoForm.coordenadas}
            onChange={(e) => handleEnderecoChange('coordenadas', e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleOpenMapModal}
                    size="small"
                    title="Selecionar no Mapa"
                  >
                    <MapIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label="Complemento"
            value={enderecoForm.complemento}
            onChange={(e) => handleEnderecoChange('complemento', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControl component="fieldset" size="small">
            <RadioGroup
              row
              value={enderecoForm.tipo_moradia}
              onChange={(e) => handleEnderecoChange('tipo_moradia', e.target.value as any)}
            >
              <FormControlLabel value="propria" control={<Radio size="small" />} label={<Typography variant="caption">Própria</Typography>} />
              <FormControlLabel value="alugado" control={<Radio size="small" />} label={<Typography variant="caption">Alugado</Typography>} />
            </RadioGroup>
          </FormControl>
        </Grid>
      </Grid>

      <Divider sx={{ my: 1.5 }} />

      <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ mb: 0.5, display: 'block' }}>
        Atualizar endereço em:
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <FormControlLabel
          control={<Checkbox checked disabled size="small" />}
          label={<Typography variant="body2">Serviço</Typography>}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={updateContrato}
              onChange={(e) => { setUpdateContrato(e.target.checked); setConfirmacaoSkip(false); setMostrarConfirmacao(false); }}
              size="small"
            />
          }
          label={<Typography variant="body2">Contrato</Typography>}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={updateCliente}
              onChange={(e) => { setUpdateCliente(e.target.checked); setConfirmacaoSkip(false); setMostrarConfirmacao(false); }}
              size="small"
            />
          }
          label={<Typography variant="body2">Cliente</Typography>}
        />
      </Box>

      {mostrarConfirmacao && (!updateContrato || !updateCliente) && (
        <Alert severity="warning" sx={{ mt: 1.5, py: 0.5 }}>
          <Typography variant="caption">
            Tem certeza que não deseja atualizar os dados
            {!updateContrato && !updateCliente
              ? ' do Contrato e do Cliente'
              : !updateContrato
                ? ' do Contrato'
                : ' do Cliente'
            }?
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={confirmacaoSkip}
                onChange={(e) => setConfirmacaoSkip(e.target.checked)}
                size="small"
              />
            }
            label={<Typography variant="caption" fontWeight="bold">Sim, tenho certeza</Typography>}
            sx={{ mt: 0.5, ml: 0 }}
          />
        </Alert>
      )}
    </Box>
  );

  // ========== Funções de parcelas ==========
  const gerarParcelas = (valorTotal: number, qtde: number) => {
    const diaVenc = contract.dia_vencimento || 10;
    const valorParcela = Math.round((valorTotal / qtde) * 100) / 100;
    const novasParcelas: { num: number; valor: number; valorFormatado: string; vencimento: string }[] = [];
    let somaAtual = 0;

    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    let mesBase = hoje.getMonth() + 1;
    let anoBase = hoje.getFullYear();

    // Se o dia de vencimento já passou este mês, começa no mês que vem
    let primeiroVenc = buildDateWithDay(diaVenc, anoBase, mesBase);
    if (primeiroVenc <= hoje) {
      mesBase++;
      if (mesBase > 12) { mesBase = 1; anoBase++; }
    }

    for (let i = 0; i < qtde; i++) {
      let mes = mesBase + i;
      let ano = anoBase;
      while (mes > 12) { mes -= 12; ano++; }

      let dataVenc = buildDateWithDay(diaVenc, ano, mes);
      dataVenc = ajustarFimDeSemana(dataVenc);
      const vencimento = toDateOnly(dataVenc);

      let vlr = valorParcela;
      if (i === qtde - 1) {
        vlr = Math.round((valorTotal - somaAtual) * 100) / 100;
      }
      somaAtual += vlr;

      novasParcelas.push({
        num: i + 1,
        valor: vlr,
        valorFormatado: vlr.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        vencimento,
      });
    }
    return novasParcelas;
  };

  const handleTaxaParcelaDateChange = (num: number, newDate: string) => {
    setTaxaParcelas((prev) =>
      prev.map((p) => (p.num === num ? { ...p, vencimento: newDate } : p))
    );
  };

  const handleTaxaValorChange = (val: string) => {
    const formatted = formatarValorOnInput(val);
    setTaxaValor(formatted);
    if (taxaParcelar && taxaQtdeParcelas > 1) {
      const valorNum = parsearMoeda(formatted);
      setTaxaParcelas(gerarParcelas(valorNum, taxaQtdeParcelas));
    }
  };

  const handleTaxaQtdeChange = (qtde: number) => {
    const q = Math.max(1, Math.min(qtde, 24));
    setTaxaQtdeParcelas(q);
    const valorNum = parsearMoeda(taxaValor);
    if (q > 1) {
      setTaxaParcelas(gerarParcelas(valorNum, q));
    } else {
      setTaxaParcelas([]);
    }
  };

  const handleTaxaParcelarChange = (ativado: boolean) => {
    setTaxaParcelar(ativado);
    if (ativado) {
      const q = taxaQtdeParcelas > 1 ? taxaQtdeParcelas : 2;
      setTaxaQtdeParcelas(q);
      const valorNum = parsearMoeda(taxaValor);
      setTaxaParcelas(gerarParcelas(valorNum, q));
    } else {
      setTaxaQtdeParcelas(1);
      setTaxaParcelas([]);
    }
  };

  const renderEnderecoStep3Financeiro = () => (
    <Box sx={{ py: 1 }}>
      <FormControlLabel
        control={
          <Switch
            checked={cobrarTaxa}
            onChange={(e) => setCobrarTaxa(e.target.checked)}
            color="primary"
          />
        }
        label={
          <Typography variant="body2" fontWeight="bold">
            Cobrar Taxa de Serviço
          </Typography>
        }
      />

      {cobrarTaxa && (
        <Box sx={{ mt: 1.5 }}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Valor *"
                value={taxaValor}
                onChange={(e) => handleTaxaValorChange(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                }}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Data Vencimento"
                type="date"
                value={(() => {
                  if (!taxaParcelar) {
                    const diaVenc = contract.dia_vencimento || 10;
                    return toDateOnly(getProximoVencimentoTaxa(diaVenc));
                  }
                  return '';
                })()}
                disabled={taxaParcelar}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Descrição *"
                value={taxaDescricao}
                onChange={(e) => setTaxaDescricao(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={8}>
              <GatewaySelect
                value={gatewayId ?? undefined}
                onChange={setGatewayId}
                label="Gateway *"
                size="small"
                fullWidth
                showOnlyActive
              />
            </Grid>
            <Grid item xs={12} sm={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={taxaParcelar}
                    onChange={(e) => handleTaxaParcelarChange(e.target.checked)}
                    color="primary"
                    size="small"
                  />
                }
                label={<Typography variant="body2">Parcelar</Typography>}
              />
            </Grid>
            {taxaParcelar && (
              <Grid item xs={12} sm={2}>
                <TextField
                  size="small"
                  label="Qtde Parcelas"
                  type="number"
                  value={taxaQtdeParcelas}
                  onChange={(e) => handleTaxaQtdeChange(Number(e.target.value) || 1)}
                  inputProps={{ min: 2, max: 24 }}
                  sx={{ width: 140 }}
                />
              </Grid>
            )}
          </Grid>


          {taxaParcelar && (
            <Box sx={{ mt: 1 }}>
              <TableContainer component={Paper} sx={{ maxHeight: 250 }} elevation={1}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.8rem' }}>Parc.</TableCell>
                      <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.8rem' }}>Vencimento</TableCell>
                      <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.8rem' }}>Valor</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {taxaParcelas.map((p) => (
                      <TableRow key={p.num}>
                        <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.8rem' }}>{p.num}/{taxaQtdeParcelas}</TableCell>
                        <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.8rem' }}>
                          <TextField
                            type="date"
                            size="small"
                            value={p.vencimento}
                            onChange={(e) => handleTaxaParcelaDateChange(p.num, e.target.value)}
                            variant="standard"
                            InputProps={{ disableUnderline: true, style: { fontSize: '0.8rem' } }}
                            sx={{ width: 120 }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.8rem' }}>R$ {p.valorFormatado}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="caption" color="primary" fontWeight="bold">
                  Total: R$ {parsearMoeda(taxaValor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );

  const renderEnderecoStep4Chamado = () => (
    <Box sx={{ py: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Deseja abrir um chamado para mudança de endereço?
      </Typography>

      <FormControlLabel
        control={
          <Switch
            checked={chamadoData.abrirChamado}
            onChange={(e) => handleChamadoChange('abrirChamado', e.target.checked)}
            color="primary"
          />
        }
        label={
          <Typography variant="body2" fontWeight="bold">
            Abrir chamado para mudança de endereço
          </Typography>
        }
      />

      {chamadoData.abrirChamado && (
        <Box sx={{ mt: 1.5 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={chamadoData.incluirEnderecoAntigo}
                onChange={(e) => handleChamadoChange('incluirEnderecoAntigo', e.target.checked)}
                size="small"
              />
            }
            label={<Typography variant="body2">Incluir endereço antigo no chamado</Typography>}
          />

          <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Prioridade</InputLabel>
                <Select
                  value={chamadoData.prioridade}
                  label="Prioridade"
                  onChange={(e) => handleChamadoChange('prioridade', e.target.value)}
                >
                  {PRIORIDADES.map((p) => (
                    <MenuItem key={p.value} value={p.value}>
                      {p.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                size="small"
                label="Quem Solicitou"
                value={chamadoData.quemSolicitou}
                onChange={(e) => handleChamadoChange('quemSolicitou', e.target.value)}
                placeholder="Nome da pessoa que solicitou"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FuncionariosSelect
                value={chamadoData.tecnicoResponsavel}
                onChange={(value) => handleChamadoChange('tecnicoResponsavel', value)}
                label="Técnico Responsável"
                placeholder="Selecione (opcional)"
                size="small"
                allowClear
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Descrição"
                multiline
                rows={3}
                value={chamadoData.descricao}
                onChange={(e) => handleChamadoChange('descricao', e.target.value)}
              />
            </Grid>
            {/* Data de Agendamento */}
            <Grid item xs={12} sm={6}>
              <SysDateTime
                label="Data de Agendamento"
                value={chamadoData.dataAgendamento}
                onChange={handleChamadoDateChange}
                type="data"
                placeholder="Selecione a data do agendamento (Opcional)"
              />
            </Grid>

            {/* Horário */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="wizard-schedule-time-label" shrink>Horário</InputLabel>
                <Select
                  labelId="wizard-schedule-time-label"
                  value={selectedScheduleTime || ''}
                  label="Horário"
                  onChange={(e) => handleChamadoScheduleTimeChange(e.target.value)}
                  disabled={!chamadoData.dataAgendamento}
                  displayEmpty
                  notched
                  sx={{ borderRadius: 1 }}
                >
                  {!chamadoData.dataAgendamento && (
                    <MenuItem value="" disabled>
                      Selecione a data primeiro
                    </MenuItem>
                  )}
                  {chamadoData.dataAgendamento && scheduleOptions.length === 0 && (
                    <MenuItem value="" disabled>
                      Sem horários disponíveis para esta data
                    </MenuItem>
                  )}
                  {chamadoData.dataAgendamento && scheduleOptions.length > 0 && (
                    <MenuItem value="" disabled>
                      Selecione o horário
                    </MenuItem>
                  )}
                  {scheduleOptions.map((option) => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      disabled={option.disabled}
                      sx={{ py: 1, '&.Mui-selected': { backgroundColor: 'action.hover' }, '&:hover': { backgroundColor: 'action.hover' } }}
                    >
                      <Box sx={{ width: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: option.tickets.length > 0 ? 0.5 : 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 48, color: 'text.primary' }}>
                            {option.value}
                          </Typography>
                          {option.tickets.length === 0 && (
                            <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
                              Disponível
                            </Typography>
                          )}
                        </Box>

                        {option.tickets.length > 0 && option.tickets.map((ticket) => (
                          <Box
                            key={`${option.value}-${ticket.id}`}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.75,
                              minWidth: 0,
                              py: 0.15,
                              flexWrap: 'wrap',
                            }}
                          >
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: getPriorityTextColor(ticket.priority),
                                boxShadow: '0 0 0 1px rgba(0,0,0,0.08)',
                                flexShrink: 0,
                              }}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                color: getPriorityTextColor(ticket.priority),
                                fontWeight: 700,
                                lineHeight: 1.2,
                              }}
                            >
                              {formatPriorityLabel(ticket.priority)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 700, lineHeight: 1.2 }}>
                              #{ticket.id}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.primary',
                                lineHeight: 1.2,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: 170,
                              }}
                            >
                              {ticket.client}
                            </Typography>
                            {ticket.tecnico && (
                              <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.2 }}>
                                ({ticket.tecnico})
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );

  const renderEnderecoStep5Finalizado = () => (
    <Box sx={{ py: 1 }}>
      <Alert severity="success" icon={<CheckCircle fontSize="small" />} sx={{ mb: 1.5 }}>
        Mudança de endereço realizada com sucesso!
      </Alert>

      {resultadoMudanca?.chamadoId ? (
        <Card variant="outlined">
          <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
            <Typography variant="caption" color="primary" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Receipt fontSize="small" />
              Chamado Aberto
            </Typography>
            <Divider sx={{ my: 0.5 }} />
            <InfoRow label="Nº Chamado" value={`#${resultadoMudanca.chamadoId}`} highlight />
            <InfoRow
              label="Prioridade"
              value={PRIORIDADES.find((p) => p.value === resultadoMudanca.prioridade)?.label || resultadoMudanca.prioridade || '—'}
            />
            <InfoRow label="Data de Criação" value={formatDateTime(resultadoMudanca.dataCriacao)} />
            <InfoRow label="Data Agendamento" value={resultadoMudanca.dataAgendamento ? formatDateTime(resultadoMudanca.dataAgendamento) : '—'} />
            <InfoRow label="Técnico Responsável" value={resultadoMudanca.tecnicoResponsavel || 'Não atribuído'} />
          </CardContent>
        </Card>
      ) : (
        <Alert severity="info" sx={{ py: 0.5 }}>
          Nenhum chamado foi aberto para esta mudança.
        </Alert>
      )}
    </Box>
  );

  // ========== Alterar Titularidade - Steps ==========
  const renderTitularidadeStep1 = () => (
    <Box sx={{ py: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Selecione o novo titular do contrato:
      </Typography>

      <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
        <Typography variant="caption">
          <strong>Titular atual:</strong> {contract.cliente?.nome || 'Não definido'} (ID: {contract.cliente_id})
        </Typography>
      </Alert>

      <ClientSelect
        value={novoClienteId}
        onChange={(clientId, client) => {
          setNovoClienteId(clientId);
          setNovoCliente(client || null);
        }}
        label="Novo Titular"
        placeholder="Buscar cliente..."
        required
        size="small"
        onlyActive={false}
        allowClear
      />

      <Box sx={{ mt: 1.5 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<PersonAdd />}
          onClick={() => setClientFormOpen(true)}
          sx={{ textTransform: 'none' }}
        >
          Cadastrar Novo Cliente
        </Button>
      </Box>

      {novoCliente && novoClienteId !== contract.cliente_id && (
        <Card variant="outlined" sx={{ mt: 1.5, borderColor: theme.palette.success.main }}>
          <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
            <Typography variant="caption" color="success.main" fontWeight="bold">
              Novo Titular Selecionado
            </Typography>
            <Divider sx={{ my: 0.5 }} />
            <InfoRow label="Nome" value={novoCliente.nome} highlight />
            <InfoRow label="CPF/CNPJ" value={novoCliente.cpf_cnpj || '—'} />
            <InfoRow label="Cidade" value={`${novoCliente.cidade || '—'}/${novoCliente.estado || '—'}`} />
          </CardContent>
        </Card>
      )}

      {novoClienteId === contract.cliente_id && novoClienteId !== null && (
        <Alert severity="warning" sx={{ mt: 1, py: 0.5 }}>
          O cliente selecionado é o mesmo do contrato atual. Selecione um cliente diferente.
        </Alert>
      )}

      <ClientForm
        open={clientFormOpen}
        onClose={() => setClientFormOpen(false)}
        onSuccess={(client: Client) => {
          setNovoClienteId(client.id);
          setNovoCliente(client);
          setClientFormOpen(false);
          toast.success(`Cliente ${client.nome} cadastrado com sucesso!`);
        }}
      />
    </Box>
  );

  const renderTitularidadeStep2 = () => (
    <Box sx={{ py: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Selecione os dados que deseja migrar para o novo titular:
      </Typography>

      <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
        <Typography variant="caption">
          Os registros marcados abaixo terão o <strong>cliente_id</strong> alterado de{' '}
          <strong>{contract.cliente?.nome}</strong> para <strong>{novoCliente?.nome}</strong>.
        </Typography>
      </Alert>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={migrarTitulosAbertos}
              onChange={(e) => setMigrarTitulosAbertos(e.target.checked)}
            />
          }
          label={
            <Box>
              <Typography variant="body2" fontWeight="bold">Migrar títulos Abertos e Vencidos</Typography>
              <Typography variant="caption" color="text.secondary">
                Faturas com status aberto, vencido e parcialmente pago deste contrato
              </Typography>
            </Box>
          }
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={migrarTitulosPagosCancelados}
              onChange={(e) => setMigrarTitulosPagosCancelados(e.target.checked)}
            />
          }
          label={
            <Box>
              <Typography variant="body2" fontWeight="bold">Migrar títulos Pagos e Cancelados</Typography>
              <Typography variant="caption" color="text.secondary">
                Faturas com status pago, cancelado e premiado deste contrato
              </Typography>
            </Box>
          }
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={migrarNotasNfcom}
              onChange={(e) => setMigrarNotasNfcom(e.target.checked)}
            />
          }
          label={
            <Box>
              <Typography variant="body2" fontWeight="bold">Migrar Notas NFCOM</Typography>
              <Typography variant="caption" color="text.secondary">
                Notas fiscais de comunicação vinculadas às faturas deste contrato
              </Typography>
            </Box>
          }
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={migrarChamados}
              onChange={(e) => setMigrarChamados(e.target.checked)}
            />
          }
          label={
            <Box>
              <Typography variant="body2" fontWeight="bold">Migrar Chamados</Typography>
              <Typography variant="caption" color="text.secondary">
                Chamados/tickets vinculados a este contrato
              </Typography>
            </Box>
          }
        />
      </Box>
    </Box>
  );

  const renderTitularidadeStep3 = () => (
    <Box sx={{ py: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Confirme os dados da alteração de titularidade:
      </Typography>

      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        <Grid item xs={12} sm={6}>
          <Card variant="outlined">
            <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
              <Typography variant="caption" color="error.main" fontWeight="bold">
                Titular Atual
              </Typography>
              <Divider sx={{ my: 0.5 }} />
              <InfoRow label="Nome" value={contract.cliente?.nome || '—'} />
              <InfoRow label="CPF/CNPJ" value={contract.cliente?.cpf_cnpj || '—'} />
              <InfoRow label="ID" value={String(contract.cliente_id)} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card variant="outlined" sx={{ borderColor: theme.palette.success.main }}>
            <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
              <Typography variant="caption" color="success.main" fontWeight="bold">
                Novo Titular
              </Typography>
              <Divider sx={{ my: 0.5 }} />
              <InfoRow label="Nome" value={novoCliente?.nome || '—'} highlight />
              <InfoRow label="CPF/CNPJ" value={novoCliente?.cpf_cnpj || '—'} />
              <InfoRow label="ID" value={String(novoClienteId)} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card variant="outlined">
        <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
          <Typography variant="caption" color="primary" fontWeight="bold">
            Dados a Migrar
          </Typography>
          <Divider sx={{ my: 0.5 }} />
          <InfoRow label="Títulos Abertos/Vencidos" value={migrarTitulosAbertos ? '✅ Sim' : '❌ Não'} />
          <InfoRow label="Títulos Pagos/Cancelados" value={migrarTitulosPagosCancelados ? '✅ Sim' : '❌ Não'} />
          <InfoRow label="Notas NFCOM" value={migrarNotasNfcom ? '✅ Sim' : '❌ Não'} />
          <InfoRow label="Chamados" value={migrarChamados ? '✅ Sim' : '❌ Não'} />
        </CardContent>
      </Card>

      <Alert severity="warning" sx={{ mt: 1.5, py: 0.5 }}>
        <Typography variant="caption">
          <strong>Atenção:</strong> Esta ação não pode ser facilmente revertida.
          Confirme que todos os dados estão corretos antes de prosseguir.
        </Typography>
      </Alert>
    </Box>
  );

  const renderPendenciasSectionTitle = (title: string, count: number, isFirst?: boolean) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, mt: isFirst ? 0 : 2 }}>
      <Typography variant="subtitle2" fontWeight="bold">
        {title}
      </Typography>
      <Chip
        size="small"
        label={count}
        color={count > 0 ? 'warning' : 'success'}
        variant="outlined"
      />
    </Box>
  );

  const renderCancelarStep1 = () => (
    <Box sx={{ py: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Consulta de pendências do contrato. Revise as tabelas abaixo. Para seguir com o cancelamento
        apesar das pendências, use o botão <strong>Cancelar mesmo assim</strong>.
      </Typography>

      {cancelarPendLoading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 3 }}>
          <CircularProgress size={28} />
          <Typography variant="body2" color="text.secondary">
            Carregando pendências...
          </Typography>
        </Box>
      )}

      {cancelarPendError && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {cancelarPendError}
        </Alert>
      )}

      {!cancelarPendLoading && !cancelarPendError && (
        <>
          {renderPendenciasSectionTitle('Faturas pendentes', cancelarFaturas.length, true)}
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 240 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Vencimento</TableCell>
                  <TableCell>Valor</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Descrição</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cancelarFaturas.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="caption" color="text.secondary">
                        Nenhuma fatura pendente neste critério.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  cancelarFaturas.map((f) => (
                    <TableRow key={f.id} hover>
                      <TableCell>{f.id}</TableCell>
                      <TableCell>{formatDate(f.data_vencimento)}</TableCell>
                      <TableCell>{formatCurrency(f.valor)}</TableCell>
                      <TableCell>
                        <InvoiceStatusChip status={f.status} size="small" />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 220 }}>
                        <Typography variant="caption" noWrap title={f.descricao}>
                          {f.descricao}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {renderPendenciasSectionTitle('Equipamentos vinculados', cancelarEquipamentos.length)}
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 240 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Modelo</TableCell>
                  <TableCell>Nº série</TableCell>
                  <TableCell>Modalidade</TableCell>
                  <TableCell align="center">Provisionamento</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cancelarEquipamentos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="caption" color="text.secondary">
                        Nenhum equipamento vinculado ao contrato.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  cancelarEquipamentos.map((eq) => (
                    <TableRow key={eq.id} hover>
                      <TableCell>{eq.id}</TableCell>
                      <TableCell>{eq.modelo?.titulo || `Modelo #${eq.modeloId}`}</TableCell>
                      <TableCell>{eq.numero_serie || '—'}</TableCell>
                      <TableCell>{eq.modalidade === 'comodato' ? 'Comodato' : 'Próprio'}</TableCell>
                      <TableCell align="center">
                        {eq.modelo?.tipo === 'ONU' || eq.modelo?.tipo === 'ONT' ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                            <Chip
                              icon={getProvisionStatusIcon(eq.provision_status || 'pending')}
                              label={getProvisionStatusLabel(eq.provision_status || 'pending')}
                              color={getProvisionStatusColor(eq.provision_status || 'pending') as 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'}
                              size="small"
                              sx={{ height: 20, fontSize: '0.65rem', '& .MuiChip-icon': { fontSize: 12 } }}
                            />
                            {eq.onu_id !== undefined && eq.onu_id !== null && (
                              <Chip
                                label={`ID: ${eq.onu_id}`}
                                color="primary"
                                size="small"
                                variant="outlined"
                                sx={{ height: 18, fontSize: '0.65rem' }}
                              />
                            )}
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {renderPendenciasSectionTitle('Chamados pendentes', cancelarChamados.length)}
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 240 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Abertura</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Prioridade</TableCell>
                  <TableCell>Descrição</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cancelarChamados.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="caption" color="text.secondary">
                        Nenhum chamado pendente neste critério.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  cancelarChamados.map((t) => (
                    <TableRow key={t.id} hover>
                      <TableCell>{t.id}</TableCell>
                      <TableCell>{formatDateTime(t.createdAt)}</TableCell>
                      <TableCell>{labelStatusChamado[t.status] || t.status}</TableCell>
                      <TableCell>
                        {PRIORIDADES.find((p) => p.value === t.prioridade)?.label || t.prioridade}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 220 }}>
                        <Typography variant="caption" noWrap title={t.descricao}>
                          {t.descricao}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );

  const renderCancelarStep2 = () => (
    <Box sx={{ py: 1 }}>
      <Alert severity="warning" sx={{ mb: 1.5 }}>
        <Typography variant="body2">
          Confirme o cancelamento do contrato. O motivo é obrigatório e será registrado no histórico.
        </Typography>
      </Alert>
      <TextField
        fullWidth
        multiline
        minRows={3}
        label="Motivo do cancelamento"
        value={motivoCancelamento}
        onChange={(e) => setMotivoCancelamento(e.target.value)}
        required
        size="small"
        placeholder="Descreva o motivo do cancelamento..."
      />
      {cancelarError && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {cancelarError}
        </Alert>
      )}
    </Box>
  );

  // ========== Render step content ==========
  const renderStepContent = () => {
    if (activeStep === 0) return renderStep0();

    if (acaoSelecionada === 'alterar_dia_vencimento') {
      switch (activeStep) {
        case 1: return renderDiaVencimentoStep1();
        case 2: return renderDiaVencimentoStep2();
        case 3: return renderDiaVencimentoStep3();
        default: return null;
      }
    }

    if (acaoSelecionada === 'mudanca_endereco') {
      switch (activeStep) {
        case 1: return renderEnderecoStep1Servico();
        case 2: return renderEnderecoStep2DadosEndereco();
        case 3: return renderEnderecoStep3Financeiro();
        case 4: return renderEnderecoStep4Chamado();
        case 5: return renderEnderecoStep5Finalizado();
        default: return null;
      }
    }

    if (acaoSelecionada === 'alterar_titularidade') {
      switch (activeStep) {
        case 1: return renderTitularidadeStep1();
        case 2: return renderTitularidadeStep2();
        case 3: return renderTitularidadeStep3();
        default: return null;
      }
    }

    if (acaoSelecionada === 'cancelar_contrato') {
      switch (activeStep) {
        case 1: return renderCancelarStep1();
        case 2: return renderCancelarStep2();
        default: return null;
      }
    }

    return null;
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { maxHeight: '85vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, px: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarMonth color="primary" fontSize="small" />
            <Typography variant="subtitle1" fontWeight="bold">
              Alteração de Contrato
            </Typography>
            <Chip label={`#${contract.id}`} size="small" variant="outlined" />
          </Box>
          <IconButton onClick={onClose} size="small" disabled={cancelarSubmitting}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        <Divider />

        {activeStep > 0 && wizardSteps.length > 0 && (
          <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
            <Stepper activeStep={activeStep - 1} alternativeLabel sx={{ '& .MuiStepLabel-label': { fontSize: '0.7rem' } }}>
              {wizardSteps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        )}

        <DialogContent sx={{ pt: 0.5, px: 2 }}>{renderStepContent()}</DialogContent>

        <Divider />

        <DialogActions sx={{ px: 2, py: 1.5 }}>
          {isFinalizado ? (
            <>
              <Box sx={{ flex: 1 }} />
              <Button
                variant="contained"
                onClick={onClose}
              >
                Fechar
              </Button>
            </>
          ) : (
            <>
              <Button onClick={onClose} color="inherit" disabled={concluindo || cancelarSubmitting}>
                Cancelar
              </Button>
              <Box sx={{ flex: 1 }} />
              {activeStep > 0 && (
                <Button
                  onClick={handleBack}
                  startIcon={<ArrowBack />}
                  disabled={concluindo || cancelarSubmitting}
                >
                  Voltar
                </Button>
              )}
              {/* Botão Concluir para alterar_dia_vencimento no último step */}
              {acaoSelecionada === 'alterar_dia_vencimento' && isLastStep && (
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleConcluirDiaVencimento}
                  disabled={concluindo}
                  startIcon={concluindo ? <CircularProgress size={20} /> : <CheckCircle />}
                >
                  {concluindo ? 'Concluindo...' : 'Concluir'}
                </Button>
              )}
              {/* Botão Próximo / Concluir para mudança de endereço */}
              {acaoSelecionada === 'mudanca_endereco' && activeStep < wizardSteps.length && (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  endIcon={activeStep === 4 ? (concluindo ? <CircularProgress size={20} /> : <CheckCircle />) : <ArrowForward />}
                  disabled={!canAdvance() || concluindo}
                  color={activeStep === 4 ? 'success' : 'primary'}
                >
                  {activeStep === 4 ? (concluindo ? 'Concluindo...' : 'Concluir') : 'Próximo'}
                </Button>
              )}
              {/* Botão Próximo para alterar_dia_vencimento (não no último step) */}
              {/* Botão Próximo/Concluir para alterar_titularidade */}
              {acaoSelecionada === 'alterar_titularidade' && activeStep <= wizardSteps.length && (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  endIcon={activeStep === 3 ? (titularidadeLoading ? <CircularProgress size={20} /> : <CheckCircle />) : <ArrowForward />}
                  disabled={!canAdvance() || titularidadeLoading}
                  color={activeStep === 3 ? 'success' : 'primary'}
                >
                  {activeStep === 3 ? (titularidadeLoading ? 'Concluindo...' : 'Concluir') : 'Próximo'}
                </Button>
              )}
              {acaoSelecionada === 'alterar_dia_vencimento' && !isLastStep && (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  endIcon={<ArrowForward />}
                  disabled={!canAdvance()}
                >
                  Próximo
                </Button>
              )}
              {acaoSelecionada === 'cancelar_contrato' && activeStep === 1 && (
                <Button
                  variant="contained"
                  color="warning"
                  onClick={() => setActiveStep(2)}
                  endIcon={<ArrowForward />}
                  disabled={cancelarPendLoading}
                >
                  Cancelar mesmo assim
                </Button>
              )}
              {acaoSelecionada === 'cancelar_contrato' && activeStep === 2 && (
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleConfirmCancelarContrato}
                  disabled={!motivoCancelamento.trim() || cancelarSubmitting}
                  startIcon={cancelarSubmitting ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
                >
                  {cancelarSubmitting ? 'Cancelando...' : 'Confirmar cancelamento'}
                </Button>
              )}

            </>
          )}
        </DialogActions>
      </Dialog>

      <MapSelectorModal
        open={mapModalOpen}
        onClose={() => setMapModalOpen(false)}
        onConfirm={handleConfirmMapLocation}
        initialCoordinates={selectedLocation}
        title="📍 Selecionar Localização no Mapa"
        description="Clique no mapa para selecionar a localização do novo endereço"
      />
    </>
  );
};

interface InfoRowProps {
  label: string;
  value: string;
  highlight?: boolean;
}

const InfoRow: React.FC<InfoRowProps> = ({ label, value, highlight }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25, gap: 1 }}>
    <Typography variant="caption" color="text.secondary" noWrap>
      {label}:
    </Typography>
    <Typography
      variant="caption"
      fontWeight={highlight ? 'bold' : 'medium'}
      color={highlight ? 'primary.main' : 'text.primary'}
      sx={{ textAlign: 'right' }}
    >
      {value}
    </Typography>
  </Box>
);

export default ContractAlteracaoWizard;
